/**
 * The tenant's portal clock, home currency and local phone codes (brief
 * 2026-10-08 §8.4, G4): read from its geography, not from Malaysian
 * constants. The API's `TenantLocale` decides them (an admin's
 * `portal_time_zone`, else the first destination's zone; the first entity's
 * currency; the destinations' phone codes) and serves them as `locale` next
 * to the company profile on `GET /api/public/company` (no sign-in, so the
 * login page and the guest pages follow) and `GET /api/company`.
 *
 * Until that answer lands, and whenever it is missing or malformed, the
 * locale is Asian Trails': Kuala Lumpur ("MYT"), ringgit printed "RM",
 * stations 60 / 65 / 673. That is what every helper hard-coded before, so
 * Asian Trails reads exactly as before.
 *
 * Module state on purpose: formatters, `portalIso()` and the phone polisher
 * are plain functions called outside React. `useTenantLocale()` re-renders
 * the components that print a label when the locale changes.
 */
import { useSyncExternalStore } from 'react';

export interface TenantLocale {
  /** IANA zone of the portal clock. */
  timeZone: string;
  /** The short name printed after a time ("MYT", "ICT", "UTC+7"). */
  timeZoneLabel: string;
  /** ISO code of the home currency. */
  currency: string;
  /** The home currency as printed ("RM" for ringgit, else the code). */
  currencyLabel: string;
  /** The destinations' phone codes, in order; the first is the default country. */
  phoneCodes: string[];
  /** Countries whose numbers are the ground team's, not a guest's. */
  localPhoneCodes: string[];
}

export const DEFAULT_TIME_ZONE = 'Asia/Kuala_Lumpur';

export const LOCALE_DEFAULTS: TenantLocale = Object.freeze({
  timeZone: DEFAULT_TIME_ZONE,
  timeZoneLabel: 'MYT',
  currency: 'MYR',
  currencyLabel: 'RM',
  phoneCodes: ['60', '65', '673'],
  localPhoneCodes: ['60', '65', '673'],
}) as TenantLocale;

let current: TenantLocale = LOCALE_DEFAULTS;
const listeners = new Set<() => void>();

function emit() {
  for (const fn of listeners) fn();
}

/** True when this runtime knows the zone (Intl throws a RangeError otherwise). */
export function isTimeZone(zone: string | null | undefined): zone is string {
  if (!zone || typeof zone !== 'string') return false;
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

const ZONE_LABELS: Record<string, string> = {
  'Asia/Kuala_Lumpur': 'MYT', 'Asia/Kuching': 'MYT', 'Asia/Singapore': 'SGT', 'Asia/Brunei': 'BNT',
  'Asia/Bangkok': 'ICT', 'Asia/Ho_Chi_Minh': 'ICT', 'Asia/Phnom_Penh': 'ICT', 'Asia/Vientiane': 'ICT',
  'Asia/Jakarta': 'WIB', 'Asia/Makassar': 'WITA', 'Asia/Jayapura': 'WIT', 'Asia/Manila': 'PHT',
  'Asia/Yangon': 'MMT', 'Asia/Hong_Kong': 'HKT', 'Asia/Tokyo': 'JST', 'Asia/Seoul': 'KST',
  'Asia/Kolkata': 'IST', 'Asia/Colombo': 'IST', 'Asia/Dubai': 'GST', 'Indian/Maldives': 'MVT',
  'Asia/Kathmandu': 'NPT', 'Etc/UTC': 'UTC', UTC: 'UTC',
};

/** Minutes the zone is ahead of UTC at `at` (480 for Kuala Lumpur). */
export function zoneOffsetMinutes(zone: string, at: Date = new Date()): number {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric',
    }).formatToParts(at);
    const n = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
    const wall = Date.UTC(n('year'), n('month') - 1, n('day'), n('hour'), n('minute'));
    const instant = Math.floor(at.getTime() / 60_000) * 60_000;
    return Math.round((wall - instant) / 60_000);
  } catch {
    return 480;
  }
}

/** "MYT" for Kuala Lumpur, "ICT" for Bangkok; "UTC+5:45" for a zone without a short name here. */
export function zoneLabelOf(zone: string): string {
  const known = ZONE_LABELS[zone];
  if (known) return known;
  const off = zoneOffsetMinutes(zone);
  if (off === 0) return 'UTC';
  const a = Math.abs(off);
  const h = Math.floor(a / 60);
  const m = a % 60;
  return `UTC${off < 0 ? '-' : '+'}${h}${m ? `:${String(m).padStart(2, '0')}` : ''}`;
}

/** A currency as printed: ringgit (MYR or a stored "RM") prints "RM", anything else its code. Blank = the home currency. */
export function currencyLabel(code?: string | null): string {
  const c = (code ?? '').trim().toUpperCase();
  if (!c) return current.currencyLabel;
  return c === 'MYR' || c === 'RM' ? 'RM' : c;
}

function codes(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const v of raw) {
    const d = String(v ?? '').replace(/\D/g, '');
    if (d.length >= 1 && d.length <= 4 && !out.includes(d)) out.push(d);
  }
  return out;
}

/**
 * The locale from the API's `locale` object (snake_case). Every missing or
 * malformed field keeps Asian Trails' value.
 */
export function normalizeLocale(raw: unknown): TenantLocale {
  if (!raw || typeof raw !== 'object') return LOCALE_DEFAULTS;
  const r = raw as Record<string, unknown>;
  const zone = typeof r.time_zone === 'string' && isTimeZone(r.time_zone.trim()) ? r.time_zone.trim() : DEFAULT_TIME_ZONE;
  const label = typeof r.time_zone_label === 'string' && r.time_zone_label.trim() ? r.time_zone_label.trim() : zoneLabelOf(zone);
  let currency = typeof r.currency === 'string' ? r.currency.trim().toUpperCase() : '';
  if (currency === 'RM') currency = 'MYR';
  if (!/^[A-Z]{3}$/.test(currency)) currency = LOCALE_DEFAULTS.currency;
  const phones = codes(r.phone_codes);
  const local = codes(r.local_phone_codes);
  return {
    timeZone: zone,
    timeZoneLabel: label,
    currency,
    currencyLabel: currency === 'MYR' ? 'RM' : currency,
    phoneCodes: phones.length ? phones : [...LOCALE_DEFAULTS.phoneCodes],
    localPhoneCodes: local.length ? local : phones.length ? phones : [...LOCALE_DEFAULTS.localPhoneCodes],
  };
}

/** Apply the API's `locale` (bootstrap, staff load, an admin save). Returns the locale now running. */
export function applyLocale(raw: unknown): TenantLocale {
  const next = normalizeLocale(raw);
  if (JSON.stringify(next) !== JSON.stringify(current)) {
    current = next;
    emit();
  }
  return current;
}

/** Back to Asian Trails' locale (tests). */
export function resetLocale(): void {
  if (current !== LOCALE_DEFAULTS) {
    current = LOCALE_DEFAULTS;
    emit();
  }
}

export function getLocale(): TenantLocale {
  return current;
}

/** The portal clock's IANA zone (Asia/Kuala_Lumpur for Asian Trails). */
export function portalTimeZone(): string {
  return current.timeZone;
}

/** The portal clock's short name ("MYT" for Asian Trails). */
export function portalZoneLabel(): string {
  return current.timeZoneLabel;
}

export function subscribeLocale(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** The running locale, re-rendering when it changes. */
export function useTenantLocale(): TenantLocale {
  return useSyncExternalStore(subscribeLocale, getLocale, getLocale);
}
